/**
 * EMR Section Action Button Component
 *
 * Renders a single action button with appropriate styling based on action kind.
 * Handles visibility, enablement, and click events.
 *
 * Single Responsibility: Render action button UI
 * All logic is handled by the hook (useEmrActionHandler)
 */

import React from 'react'
import { Button } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import CloseIcon from '@mui/icons-material/Close'
import UndoIcon from '@mui/icons-material/Undo'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

// Map icon names to Material-UI icons
const ICON_MAP = {
  plus: AddIcon,
  pencil: EditIcon,
  x: CloseIcon,
  undo: UndoIcon,
  'check-all': CheckCircleIcon,
}

export default function EmrSectionActionButton({
  action,
  context = {},
  onExecute,
  shouldShow = true,
  isEnabled = true,
}) {
  // Don't render if not visible
  if (!shouldShow) {
    return null
  }

  const {
    label,
    icon,
    kind = 'api',
  } = action

  /**
   * Handle button click
   */
  const handleClick = () => {
    if (isEnabled && onExecute) {
      onExecute(action, context)
    }
  }

  /**
   * Get button variant based on action kind and intent
   */
  const getButtonVariant = () => {
    // Dialog actions typically use outlined style
    if (kind === 'dialog') {
      return 'outlined'
    }

    // Event actions use text style
    if (kind === 'event') {
      return 'text'
    }

    // API actions - check intent for styling
    if (action.intent?.includes('add') || action.intent?.includes('addToPlan')) {
      return 'contained'
    }

    // Default to contained for primary actions
    return 'contained'
  }

  /**
   * Get button color based on action intent
   */
  const getButtonColor = () => {
    if (action.intent?.includes('dismiss')) {
      return 'error'
    }

    if (action.intent?.includes('undo')) {
      return 'inherit'
    }

    return 'primary'
  }

  /**
   * Get button size
   */
  const getButtonSize = () => {
    return 'small'
  }

  /**
   * Get start icon component
   */
  const getStartIcon = () => {
    if (!icon) return null

    const IconComponent = ICON_MAP[icon]
    return IconComponent ? <IconComponent fontSize="small" /> : null
  }

  return (
    <Button
      variant={getButtonVariant()}
      color={getButtonColor()}
      size={getButtonSize()}
      onClick={handleClick}
      disabled={!isEnabled}
      startIcon={getStartIcon()}
      sx={{
        textTransform: 'none',
        minWidth: 'auto',
      }}
    >
      {label || action.id}
    </Button>
  )
}
